import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Trash2, Edit, Plus, Upload, Download, Tag } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { KnowledgeDocument } from './types';
import { useAiManagement } from '@/hooks/use-ai-management';
import { ImportKnowledgeDialog } from './dialogs/ImportKnowledgeDialog';
import { ExportKnowledgeDialog } from './dialogs/ExportKnowledgeDialog';
import { BulkEditDialog } from './dialogs/BulkEditDialog';
import { TagAnalyticsDashboard } from './TagAnalyticsDashboard';
import { AdvancedTagSearch } from './AdvancedTagSearch';
import { DocumentsTab } from './DocumentsTab';
import { DocumentDialog } from './DocumentDialog';
import { chunkDocument, generateChunkEmbeddings, storeDocumentChunks } from '@/services/ai-content-chunking-service';

interface KnowledgeFormData {
  title: string;
  content: string;
  topic: string;
  language: string;
  tags: string[];
  isMultiLanguage: boolean;
  sourceLanguage: string;
  supportedLanguages: string[];
}

export const KnowledgeManagement: React.FC = () => {
  const [documents, setDocuments] = useState<KnowledgeDocument[]>([]);
  const [filteredDocuments, setFilteredDocuments] = useState<KnowledgeDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingDocument, setEditingDocument] = useState<KnowledgeDocument | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showBulkEditDialog, setShowBulkEditDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [topicFilter, setTopicFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('documents');
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [searchLoading, setSearchLoading] = useState(false);

  const [formData, setFormData] = useState<KnowledgeFormData>({
    title: '',
    content: '',
    topic: 'guide',
    language: 'en',
    tags: [],
    isMultiLanguage: false,
    sourceLanguage: 'en',
    supportedLanguages: ['en', 'vi']
  });

  const { generateEmbedding, isGeneratingEmbedding } = useAiManagement();

  useEffect(() => {
    fetchDocuments();
  }, [currentPage, pageSize, topicFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.trim()) {
        performSearch();
      } else {
        fetchDocuments();
      }
    }, 300); // Debounce search

    return () => clearTimeout(timer);
  }, [searchQuery, currentPage, pageSize, topicFilter]);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      // Build query with topic filter
      let countQuery = supabase
        .from('assistant_knowledge')
        .select('*', { count: 'exact', head: true });

      let dataQuery = supabase
        .from('assistant_knowledge')
        .select('*')
        .order('created_at', { ascending: false });

      // Apply topic filter if not 'all'
      if (topicFilter !== 'all') {
        countQuery = countQuery.eq('metadata->>topic', topicFilter);
        dataQuery = dataQuery.eq('metadata->>topic', topicFilter);
      }

      // Get total count first
      const { count, error: countError } = await countQuery;
      if (countError) throw countError;
      setTotalCount(count || 0);

      // Get paginated documents
      const startRange = (currentPage - 1) * pageSize;
      const endRange = startRange + pageSize - 1;
      
      const { data, error } = await dataQuery.range(startRange, endRange);
      if (error) throw error;
      
      setDocuments(data || []);
      setFilteredDocuments(data || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to fetch knowledge documents');
    } finally {
      setLoading(false);
    }
  };

  const performSearch = async () => {
    if (!searchQuery.trim()) {
      fetchDocuments();
      return;
    }

    setSearchLoading(true);
    try {
      const startRange = (currentPage - 1) * pageSize;
      const endRange = startRange + pageSize - 1;

      // Build search query with topic filter
      let query = supabase
        .from('assistant_knowledge')
        .select('*', { count: 'exact' })
        .or(`title.ilike.%${searchQuery}%,document->>content.ilike.%${searchQuery}%`)
        .order('created_at', { ascending: false })
        .range(startRange, endRange);

      // Apply topic filter if not 'all'
      if (topicFilter !== 'all') {
        query = query.eq('metadata->>topic', topicFilter);
      }

      const { data, error, count } = await query;
      
      if (error) throw error;

      setFilteredDocuments(data || []);
      setTotalCount(count || 0);
    } catch (error) {
      console.error('Error searching documents:', error);
      toast.error('Failed to search documents');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleAdvancedSearchResults = (results: KnowledgeDocument[]) => {
    setFilteredDocuments(results);
    setTotalCount(results.length);
    setCurrentPage(1); // Reset to first page for search results
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when page size changes
  };

  const handleTopicFilterChange = (topic: string) => {
    setTopicFilter(topic);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const saveDocument = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error('Title and content are required');
      return;
    }

    setLoading(true);
    try {
      const text = `${formData.title}\n\n${formData.content}`;
      
      // Check if document needs chunking (> 1000 characters)
      if (text.length > 1000) {
        console.log('[KNOWLEDGE] Document is large, using chunking service');
        
        // Generate unique document ID
        const documentId = crypto.randomUUID();
        
        // Chunk the document with enhanced options
        const chunks = chunkDocument(documentId, text, {
          maxChunkSize: 1000,
          overlapSize: 200,
          preserveStructure: true,
          language: formData.language
        });
        
        console.log(`[KNOWLEDGE] Created ${chunks.length} chunks for document`);
        
        // Generate embeddings for all chunks with progress tracking
        const chunksWithEmbeddings = await generateChunkEmbeddings(chunks, (completed, total) => {
          console.log(`[KNOWLEDGE] Embedding progress: ${completed}/${total}`);
        });
        
        // Store chunks in knowledge base
        const storeSuccess = await storeDocumentChunks(
          chunksWithEmbeddings,
          formData.topic,
          {
            title: formData.title,
            tags: formData.tags,
            is_multi_language: formData.isMultiLanguage,
            source_language: formData.sourceLanguage,
            supported_languages: formData.isMultiLanguage ? formData.supportedLanguages : [formData.language],
            document_type: 'chunked',
            original_length: text.length,
            chunk_count: chunks.length
          }
        );
        
        if (!storeSuccess) {
          throw new Error('Failed to store document chunks');
        }
        
        toast.success(`Document chunked into ${chunks.length} pieces and saved successfully`);
      } else {
        console.log('[KNOWLEDGE] Document is small, saving as single document');
        
        // Generate single embedding for small documents
        const embedding = await generateEmbedding(text);
        
        if (!embedding) {
          throw new Error('Failed to generate embedding');
        }

        const documentData = {
          title: formData.title,
          document: { content: formData.content },
          metadata: {
            topic: formData.topic,
            language: formData.language,
            tags: formData.tags,
            is_multi_language: formData.isMultiLanguage,
            source_language: formData.sourceLanguage,
            supported_languages: formData.isMultiLanguage ? formData.supportedLanguages : [formData.language],
            document_type: 'single',
            original_length: text.length
          },
          embedding: embedding
        };

        if (editingDocument) {
          const { error } = await supabase
            .from('assistant_knowledge')
            .update(documentData)
            .eq('id', editingDocument.id);

          if (error) throw error;
          toast.success('Document updated successfully');
        } else {
          const { error } = await supabase
            .from('assistant_knowledge')
            .insert(documentData);

          if (error) throw error;
          toast.success('Document added successfully');
        }
      }

      resetForm();
      fetchDocuments();
    } catch (error) {
      console.error('Error saving document:', error);
      toast.error('Failed to save document');
    } finally {
      setLoading(false);
    }
  };

  const deleteDocument = async (id: string) => {
    try {
      const { error } = await supabase
        .from('assistant_knowledge')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success('Document deleted successfully');
      fetchDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Failed to delete document');
    }
  };

  const editDocument = (document: KnowledgeDocument) => {
    setFormData({
      title: document.title || '',
      content: document.document?.content || '',
      topic: document.metadata?.topic || 'guide',
      language: document.metadata?.language || 'en',
      tags: document.metadata?.tags || [],
      isMultiLanguage: document.metadata?.is_multi_language || false,
      sourceLanguage: document.metadata?.source_language || 'en',
      supportedLanguages: document.metadata?.supported_languages || ['en']
    });
    setEditingDocument(document);
    setShowAddDialog(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      topic: 'guide',
      language: 'en',
      tags: [],
      isMultiLanguage: false,
      sourceLanguage: 'en',
      supportedLanguages: ['en', 'vi']
    });
    setEditingDocument(null);
    setShowAddDialog(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Knowledge Base Management</h2>
        <div className="flex space-x-2">
          <Button onClick={() => setShowImportDialog(true)} variant="outline">
            <Upload className="h-4 w-4 mr-2" />
            Import
          </Button>
          <Button onClick={() => setShowExportDialog(true)} variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button onClick={() => setShowBulkEditDialog(true)} variant="outline">
            <Edit className="h-4 w-4 mr-2" />
            Bulk Edit
          </Button>
          <Button onClick={() => setShowAddDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Document
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="analytics">Tag Analytics</TabsTrigger>
          <TabsTrigger value="search">Advanced Search</TabsTrigger>
        </TabsList>

        <TabsContent value="documents" className="space-y-4">
          <DocumentsTab
            filteredDocuments={filteredDocuments}
            loading={loading}
            searchQuery={searchQuery}
            searchLoading={searchLoading}
            currentPage={currentPage}
            pageSize={pageSize}
            totalCount={totalCount}
            topicFilter={topicFilter}
            onSearchChange={setSearchQuery}
            onTopicFilterChange={handleTopicFilterChange}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            onEditDocument={editDocument}
            onDeleteDocument={deleteDocument}
          />
        </TabsContent>

        <TabsContent value="analytics">
          <TagAnalyticsDashboard />
        </TabsContent>

        <TabsContent value="search" className="space-y-4">
          <AdvancedTagSearch onSearchResults={handleAdvancedSearchResults} />
          
          {/* Search Results */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Tag className="h-5 w-5 mr-2" />
                Search Results ({filteredDocuments.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {filteredDocuments.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No documents match your search criteria
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredDocuments.map((doc) => (
                    <div key={doc.id} className="border rounded-lg p-4 space-y-2">
                      <div className="flex justify-between items-start">
                        <h3 className="font-medium">{doc.title}</h3>
                        <div className="flex space-x-1">
                          <Button variant="outline" size="sm" onClick={() => editDocument(doc)}>
                            <Edit className="h-3 w-3" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => deleteDocument(doc.id)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Badge variant="outline">{doc.metadata?.topic}</Badge>
                        <Badge variant="outline">{doc.metadata?.language}</Badge>
                        {doc.metadata?.tags?.map((tag: string) => (
                          <Badge key={tag} variant="secondary">{tag}</Badge>
                        ))}
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {doc.document?.content?.substring(0, 200)}...
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <DocumentDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        editingDocument={editingDocument}
        formData={formData}
        setFormData={setFormData}
        loading={loading}
        isGeneratingEmbedding={isGeneratingEmbedding}
        onSave={saveDocument}
        onCancel={resetForm}
      />

      <ImportKnowledgeDialog
        open={showImportDialog}
        onOpenChange={setShowImportDialog}
        onImportComplete={fetchDocuments}
      />

      <ExportKnowledgeDialog
        open={showExportDialog}
        onOpenChange={setShowExportDialog}
        documents={documents}
      />

      <BulkEditDialog
        open={showBulkEditDialog}
        onOpenChange={setShowBulkEditDialog}
        onComplete={fetchDocuments}
      />
    </div>
  );
};
